import { Button, Form, Input, message, Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { titleAutoGeneratorService } from '../service/titleAutoGeneratorService';
import {
  AutomationTypeOptions,
  CreateTitleAutoGeneratorRequest,
  TitleAutoGenerator,
} from '../type/titleAutoGeneratorType';

const TitleAutoGeneratorForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<TitleAutoGenerator | null>(null);
  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode && id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await titleAutoGeneratorService.fetchById(Number(id));
      setInitialData(data);
      form.setFieldsValue({
        slug: data.slug,
        automation_type: data.automation_type,
        note: data.note,
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (isEditMode && id) {
        // 수정 모드
        await titleAutoGeneratorService.update(Number(id), {
          automation_type: values.automation_type,
          note: values.note || null,
        });
        message.success('수정되었습니다.');
        // 페이지 새로고침하여 최신 데이터 반영
        await loadData();
      } else {
        // 생성 모드
        const requestData: CreateTitleAutoGeneratorRequest = {
          slug: values.slug,
          automation_type: values.automation_type,
          note: values.note || null,
        };
        const result = await titleAutoGeneratorService.create(requestData);
        message.success('생성되었습니다.');
        navigate(`/title-auto-generator/edit/${result.id}`);
      }
    } catch (error: any) {
      console.error('Failed to submit:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        Object.keys(errorData).forEach(key => {
          message.error(`${key}: ${errorData[key].join(', ')}`);
        });
      } else {
        message.error('저장에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Component.BasePageComponent>
      <div>
        <div className="season-details-page">
          <h1>{isEditMode ? 'Edit Title Auto Generator' : 'Add Title Auto Generator'}</h1>
          <Form form={form} layout="vertical" className="season-details-form">
            <Form.Item
              name="slug"
              label="Season Slug"
              rules={[
                { required: true, message: 'Season slug를 입력해주세요.' },
                { max: 200, message: 'Season slug는 최대 200자까지 입력 가능합니다.' },
                {
                  pattern: /^[a-zA-Z0-9-_]+$/,
                  message: 'Season slug는 영문, 숫자, 하이픈, 언더스코어만 사용 가능합니다.',
                },
              ]}
              help={
                isEditMode
                  ? '⚠️ Season slug는 수정할 수 없습니다.'
                  : 'Season의 고유 slug를 입력하세요.'
              }
            >
              <Input
                placeholder="예: morning-news"
                disabled={isEditMode}
                style={isEditMode ? { backgroundColor: '#f5f5f5' } : {}}
              />
            </Form.Item>

            <Form.Item
              name="automation_type"
              label="Automation Type"
              rules={[{ required: true, message: 'Automation Type을 선택해주세요.' }]}
              help="제목 자동 생성 방식을 선택하세요."
            >
              <Select placeholder="Automation Type을 선택하세요" options={AutomationTypeOptions} />
            </Form.Item>

            <Form.Item
              name="note"
              label="Note (Optional)"
              rules={[{ max: 200, message: 'Note는 최대 200자까지 입력 가능합니다.' }]}
              help="추가 메모를 입력하세요. (최대 200자)"
            >
              <Input.TextArea
                placeholder="예: 아침 뉴스 프로그램"
                rows={4}
                maxLength={200}
                showCount
              />
            </Form.Item>

            {isEditMode && initialData && (
              <>
                <Form.Item label="Created">
                  <Input
                    value={new Date(initialData.created).toLocaleString('ko-KR')}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>

                <Form.Item label="Modified">
                  <Input
                    value={new Date(initialData.modified).toLocaleString('ko-KR')}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </div>

        <div className="season-detail-update-button">
          <Button onClick={() => navigate('/title-auto-generator')} style={{ marginRight: '10px' }}>
            취소
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {isEditMode ? '수정' : '생성'}
          </Button>
        </div>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(TitleAutoGeneratorForm);
